(() => {
  if (customElements.get('embla-carousel')) return;

  const emblaReady = new Promise((resolve) => {
    if (window.EmblaCarousel) return resolve();
    const done = () => window.EmblaCarousel && resolve();
    document.addEventListener('DOMContentLoaded', done, { once: true });
    window.addEventListener('load', done, { once: true });
  });

  class EmblaCarouselElement extends HTMLElement {
    connectedCallback() {
      emblaReady.then(() => this.setup());
    }

    disconnectedCallback() {
      this.destroyCarousel();
      this.mediaQuery?.removeEventListener('change', this.handleMediaChange);
      this._isSetup = false;
    }

    setup() {
      if (!this.isConnected || this._isSetup) return;
      this._isSetup = true;

      this.isMobileOnly = this.dataset.mobileOnly === 'true';
      this.isDesktopOnly = this.dataset.desktopOnly === 'true';

      if (this.isMobileOnly || this.isDesktopOnly) {
        this.mediaQuery = window.matchMedia('(min-width: 992px)');
        this.handleMediaChange = this.handleMediaChange.bind(this);
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
        this.handleMediaChange();
      } else {
        this.initCarousel();
      }
    }

    handleMediaChange() {
      const isDesktop = this.mediaQuery.matches;
      const shouldBeActive = this.isDesktopOnly ? isDesktop : !isDesktop;
      if (shouldBeActive) {
        if (!this.emblaApi) this.initCarousel();
      } else {
        this.destroyCarousel();
      }
    }

    cloneSlides(container) {
      const originals = [...container.children];
      while (container.scrollWidth <= container.parentElement.offsetWidth * 2) {
        originals.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input, select, textarea, video')
            .forEach((el) => el.setAttribute('tabindex', '-1'));
          container.appendChild(clone);
        });
      }
    }

    initCarousel() {
      const root = this.querySelector('.embla');
      const viewport = root?.querySelector('.embla__viewport');
      if (!viewport) return;

      const prevBtn = root.querySelector('.embla__button--prev');
      const nextBtn = root.querySelector('.embla__button--next');
      const dotsNode = root.querySelector('.embla__dots');
      const currentNode = root.querySelector('[data-embla-current]');
      const totalNode = root.querySelector('[data-embla-total]');
      const autoplayDelay = Number(this.dataset.autoplayInterval || 0);

      if (this.dataset.clone === 'true') {
        this.cloneSlides(viewport.querySelector('.embla__container'));
      }

      const options = {
        align: this.dataset.align || 'center',
        containScroll: 'trimSnaps',
        loop: this.dataset.loop !== 'false'
      };

      const plugins = [];
      if (window.EmblaCarouselClassNames) {
        plugins.push(EmblaCarouselClassNames({ snapped: 'is-snapped', inView: 'is-in-view' }));
      }

      this.autoplayPlugin = autoplayDelay > 0 && window.EmblaCarouselAutoplay
        ? EmblaCarouselAutoplay({ delay: autoplayDelay, playOnInit: true, stopOnInteraction: false, stopOnFocusIn: true })
        : null;
      if (this.autoplayPlugin) plugins.push(this.autoplayPlugin);

      this.emblaApi = EmblaCarousel(viewport, options, plugins);

      const resetAutoplay = () => this.autoplayPlugin?.reset();

      if (prevBtn && nextBtn) this.emblaApi.on('destroy', this.bindPrevNext(prevBtn, nextBtn, resetAutoplay));
      if (dotsNode) this.emblaApi.on('destroy', this.bindDots(dotsNode, resetAutoplay));
      if (currentNode && totalNode) this.emblaApi.on('destroy', this.bindIndicator(currentNode, totalNode));
    }

    bindPrevNext(prevBtn, nextBtn, onClick) {
      const api = this.emblaApi;
      const onPrev = () => { api.scrollPrev(); onClick(); };
      const onNext = () => { api.scrollNext(); onClick(); };
      const toggleDisabled = () => {
        prevBtn.toggleAttribute('disabled', !api.canScrollPrev());
        nextBtn.toggleAttribute('disabled', !api.canScrollNext());
      };

      prevBtn.addEventListener('click', onPrev);
      nextBtn.addEventListener('click', onNext);
      api.on('init', toggleDisabled).on('reInit', toggleDisabled).on('select', toggleDisabled);
      toggleDisabled();

      return () => {
        prevBtn.removeEventListener('click', onPrev);
        nextBtn.removeEventListener('click', onNext);
        prevBtn.removeAttribute('disabled');
        nextBtn.removeAttribute('disabled');
      };
    }

    bindDots(dotsNode, onClick) {
      const api = this.emblaApi;
      let dots = [];

      const build = () => {
        dotsNode.innerHTML = api.scrollSnapList()
          .map((_, i) => `<button class="embla__dot" type="button" aria-label="Go to slide ${i + 1}"></button>`)
          .join('');
        dots = [...dotsNode.querySelectorAll('.embla__dot')];
        dots.forEach((dot, i) => dot.addEventListener('click', () => { api.scrollTo(i); onClick(); }));
      };

      const select = () => {
        const selected = api.selectedScrollSnap();
        dots.forEach((dot, i) => dot.classList.toggle('embla__dot--selected', i === selected));
      };

      api.on('init', build).on('reInit', build)
         .on('init', select).on('reInit', select).on('select', select);
      build();
      select();

      return () => { dotsNode.innerHTML = ''; };
    }

    bindIndicator(currentNode, totalNode) {
      const api = this.emblaApi;
      const wrapper = currentNode.closest('.hero-media-indicator');

      const update = () => {
        const total = api.scrollSnapList().length;
        currentNode.textContent = total ? String(api.selectedScrollSnap() + 1) : '';
        totalNode.textContent = total ? String(total) : '';
        wrapper?.classList.toggle('is-ready', total > 0);
      };

      api.on('init', update).on('reInit', update).on('select', update);
      update();

      return () => {
        currentNode.textContent = '';
        totalNode.textContent = '';
        wrapper?.classList.remove('is-ready');
      };
    }

    destroyCarousel() {
      if (!this.emblaApi) return;
      this.emblaApi.destroy();
      this.emblaApi = null;
    }
  }

  customElements.define('embla-carousel', EmblaCarouselElement);
})();